import { Observable, from, firstValueFrom, of } from 'rxjs';
import { debounceTime, filter, take, timeout } from 'rxjs/operators';

/**
 * Read a progressively-loaded entity cache once it has actually finished loading.
 *
 * The entity services page their backends through
 * `PaginationService.getAllPaginatedDataProgressively()`, which `scan()`s pages
 * into a cumulative array and pushes EVERY intermediate result into the
 * BehaviorSubject. So `allParties$` doesn't emit once with 4,500 parties — it
 * emits ~45 times with 100, 200, 300… On top of that, the subject starts at
 * `[]` before the first page ever returns. That makes all three naive reads
 * wrong:
 *
 *   - `take(1)`                    → whatever page happened to have landed
 *   - `filter(a => a.length > 0)`  → page 1, every time
 *   - `debounceTime` alone         → the initial `[]`, if the request is slow
 *
 * Each one silently reports "not found" for records that exist. This resolves
 * two phases instead:
 *
 *   1. Wait for the cache to hold anything at all (up to `emptyDeadlineMs`).
 *      Hitting that deadline means the cache is genuinely empty — an org with
 *      no BAAs is a normal state, not an error — so return what's there.
 *   2. Once data is flowing, wait for `quietMs` with no new emission, which for
 *      a paging stream means the last page has landed.
 *
 * Never rejects and never hangs on a BehaviorSubject-backed source: both phases
 * are bounded, and each fallback reads the current value. Callers get partial
 * data in the pathological case rather than a promise that never settles (the
 * spinner-deadlock failure mode described in CLAUDE.md's onboarding gap #3).
 *
 * Only safe for observables that always have a current value (a BehaviorSubject
 * or `.asObservable()` over one). A plain Subject would make the fallbacks hang.
 */
export function settledCache<T>(
  obs$: Observable<T[]>,
  quietMs = 400,
  emptyDeadlineMs = 6000,
  maxWaitMs = 30000
): Promise<T[]> {
  const current = () => firstValueFrom(obs$.pipe(take(1)));

  // Phase 1 — is anything loading at all?
  return firstValueFrom(
    obs$.pipe(
      filter((items) => items.length > 0),
      take(1),
      timeout({ first: emptyDeadlineMs, with: () => of(null) })
    )
  ).then((firstNonEmpty) => {
    // Genuinely empty (or nothing is loading): report it rather than stalling
    // the caller for the full pagination budget.
    if (firstNonEmpty === null) return current();

    // Phase 2 — data is arriving; wait for the stream to go quiet. The budget
    // here has to cover a whole multi-page load, not just one gap.
    return firstValueFrom(
      obs$.pipe(
        debounceTime(quietMs),
        take(1),
        timeout({ first: maxWaitMs, with: () => obs$.pipe(take(1)) })
      )
    );
  });
}

/**
 * Observable flavour of `settledCache()`, for callers that are already inside an
 * RxJS chain and shouldn't be restructured into promises just to read a cache.
 *
 * Emits once and completes, so it drops into `switchMap` / `subscribe` exactly
 * where a `.pipe(filter(a => a.length > 0), take(1))` used to sit.
 */
export function settledCache$<T>(
  obs$: Observable<T[]>,
  quietMs = 400,
  emptyDeadlineMs = 6000,
  maxWaitMs = 30000
): Observable<T[]> {
  return from(settledCache(obs$, quietMs, emptyDeadlineMs, maxWaitMs));
}
